import { Page, PageBody, ToastContainer } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { ErrorState } from '@pypestream/ui-patterns';
import { FC, useEffect, useState } from 'react';
import {
  isProduction,
  sentryWrapCreateBrowserRouter,
  useCreateOnceAfter,
} from '@pypestream/utils';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  createRoutesFromElements,
  matchRoutes,
  Navigate,
  Outlet,
  Route,
  useLocation,
  useNavigate,
  useNavigationType,
  RouterProvider as ReactRouterProvider,
} from 'react-router';

import { Loader, PrimaryNav, ErrorBoundary } from './components';
import { CollabSessionHost } from './collab';
import { centeredContainerStyles } from './components/loader';
import { AccessSetup } from './components/access-setup';
import { RootBody } from './components/root-body';
import { SolutionOpenFailureModal } from './components/solution-open-failure/solution-open-failure-modal';
import { useLiveRegionReady } from './hooks/use-live-region-ready';
import {
  StudioPageProps,
  useAgentVocabulary,
  useCurrentRoute,
  useLoadingState,
  useResetScrollOnRouteChange,
  useProStudioRoutes,
  useTableStoreSync,
  usePublishStatusWatcherForCoworkers,
  useSolutionDeployments,
  useValidationModeToast,
  useAccessGrantedToastOnOpen,
  useSolutionDeletedSync,
} from './hooks';
import { useKnowledgeSetup } from './pages/talk/use-knowledge-setup';
import {
  AdvancedSettings,
  DataPrivacy,
  LiveAgentPlatforms,
  NotFound,
  ProjectSettings,
  ReportingApiCredentials,
} from './pages';
import {
  ActionsRedirect,
  ActionsTab,
  AssetsTab,
  MediaTab,
} from './pages/files';
import { DEFAULT_PROJECT_SETTINGS_SECTION } from './pages/project-settings';
import { resolvePageLoadView } from './hooks/loading-states';
import { syncXstateWithRouteParams } from './utils';
import {
  sendProStudioEvent,
  sendUserEvent,
  useProStudioCtxSelector,
  useProStudioStateMatches,
  useUserStateMatches,
} from './xstate/app.xstate';

const Root: FC = () => {
  useAgentVocabulary(); // Project Bobcat, Proposal 1 — flag-gated copy overlay
  useTableStoreSync();
  // Feeds ctx.solutionDeployments; must be mounted before its consumers read it.
  useSolutionDeployments();
  useKnowledgeSetup(); // Talk it through's keys + knowledge base, after create
  usePublishStatusWatcherForCoworkers();
  useValidationModeToast();
  useSolutionDeletedSync(); // PSTUDIO-3671
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    home: { path: homePath },
  } = useProStudioRoutes();

  const isAccessDenied = useProStudioStateMatches('accessDenied');
  const isSolutionNotFound = useProStudioStateMatches('solutionNotFound'); // PSTUDIO-3671
  // PSTUDIO-4098. The access screen below arms the "Access granted" toast on a
  // grant; it is shown here, once the project has actually loaded, because the
  // screen itself unmounts as soon as the machine leaves `accessDenied`.
  const isProjectOpen = useProStudioStateMatches(
    'checkingUserInfo.projectFlow.projectDataLoaded'
  );
  const armAccessGrantedToast = useAccessGrantedToastOnOpen(isProjectOpen);

  const { projectId } = useProStudioCtxSelector((ctx) => ({
    projectId: ctx.project?.id,
  }));

  useResetScrollOnRouteChange();

  useEffect(() => {
    if (pathname === '/' && homePath) {
      navigate(homePath);
    }
  }, [pathname, projectId, homePath, navigate]);

  // PSTUDIO-3671: the open solution was deleted (in this or another tab, or on
  // another machine) → the machine routed to #solutionNotFound (toast shown on
  // entry). Redirect to All Solutions (`/:projectId`, botId dropped). The
  // machine PARKS in #solutionNotFound — this same-project redirect is not a
  // "meaningful change" for syncXstateWithRouteParams, so it dispatches no
  // setParams and the machine does NOT leave the state on its own. That is why
  // #solutionNotFound is in NON_LOADING_STUDIO_STATES: the rendered All
  // Solutions page must show its content, not the full-page loader, while
  // parked. The machine only moves on once the user navigates to a solution.
  useEffect(() => {
    if (isSolutionNotFound && homePath) {
      navigate(homePath);
    }
  }, [isSolutionNotFound, homePath, navigate]);

  return (
    <ErrorBoundary>
      {/* PSTUDIO-4449: the draft's live session outlives page switches, so
          the draft's undo history survives a trip to another page. */}
      <CollabSessionHost>
        <Page>
          <PrimaryNav />

          <RootBody
            main={
              isAccessDenied ? (
                // Keyed by project: a different project in the URL starts a fresh
                // assign → wait → re-read cycle instead of reusing this one's state.
                <AccessSetup
                  key={projectId ?? ''}
                  onGranted={armAccessGrantedToast}
                />
              ) : (
                <Outlet />
              )
            }
            // Mounted here, not in a page: an open ps-modal moves its element
            // into a portal (`ModalWC._openModal` → `update-portal-content`), and
            // a page wrapper that unmounts on navigation would then removeChild
            // a node that is no longer its child. Root does not unmount between
            // pages. RootBody covers the other direction (insertBefore).
            overlay={<SolutionOpenFailureModal />}
          />
          <ToastContainer stacked position="bottom-right" />
        </Page>
      </CollabSessionHost>
    </ErrorBoundary>
  );
};

// What `reactRouterBrowserTracingIntegration` needs to name transactions by
// route pattern; main.tsx hands it to the Sentry init. Mirrors manager
// (router.tsx), so both apps wire Sentry's router tracing the same way
// (PSTUDIO-4288).
export const sentryReactRouterBindings = {
  useEffect,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
};

export const RouterProvider: FC<{ sentryReady: Promise<void> }> = ({
  sentryReady,
}) => {
  const routes = useProStudioRoutes();
  // The router is created after `sentryReady` — the Sentry gate's decision —
  // because `Sentry.wrapCreateBrowserRouter` reads its tracing bindings when it
  // wraps: a router wrapped before `Sentry.init` is never instrumented and every
  // transaction is named by raw URL. The wait is bounded by the gate's timeout
  // and is immediate in dev or without a DSN.
  //
  // Router must be created exactly once (useMemo is only a cache and may be
  // recomputed). Recreating it on every render (which happens
  // whenever XState ctx changes, since useProStudioRoutes reads ctx) causes
  // in-flight navigations to be lost — visible as the URL updating but the
  // rendered route staying on the previous page until refresh.
  // Route schemas and elements are static, so freezing the initial routes
  // object is safe.
  //
  // `useTransitions={false}` keeps router updates out of React.startTransition,
  // which React Router 7 otherwise uses by default. The XState selectors here are
  // plain setState subscriptions, so under a transition every machine update made
  // during a navigation commits first, against the OLD route params — e.g. the
  // channels page reacting to a newly selected survey solution by navigating to
  // the previous solution's versions page. This restores the single synchronous
  // commit React Router 6 had.
  const router = useCreateOnceAfter(sentryReady, () =>
    sentryWrapCreateBrowserRouter(createBrowserRouter)(
      createRoutesFromElements(
        <Route
          path="/"
          element={<Root />}
          loader={({ params, request }) => {
            const url = new URL(request.url);
            const debugValue = url.searchParams.get('debug');

            let debugMode: boolean | undefined = undefined;

            if (debugValue === 'on' || debugValue === 'true') {
              debugMode = true;
            } else if (debugValue === 'off' || debugValue === 'false') {
              debugMode = false;
            } else {
              debugMode = !isProduction;
            }

            syncXstateWithRouteParams({
              params,
              url: new URL(request.url),
              debugMode,
            });
            return null;
          }}
          shouldRevalidate={() => {
            return true;
          }}
        >
          <Route index element={routes.home.element} />
          {Object.entries(routes)
            // `files` is declared explicitly below as a nested layout route. Leaving
            // it here too would declare `:projectId/:botId/files` twice and the
            // second declaration would never match.
            .filter(([key]) => key !== 'files')
            .map(([, { schema: path, element, label }]) => (
              <Route path={path} element={element} key={label} />
            ))}
          {/* Files is a nested layout route (tab bar + <Outlet/>), same shape as
              Project Settings below. */}
          <Route path=":projectId/:botId/files" element={routes.files.element}>
            <Route index element={<Navigate to="actions" replace />} />
            <Route path="actions" element={<ActionsTab />} />
            <Route path="media" element={<MediaTab />} />
            <Route path="assets" element={<AssetsTab />} />
          </Route>
          {/* The Actions page became a Files tab; keep old links working. */}
          <Route
            path=":projectId/:botId/actions"
            element={<ActionsRedirect />}
          />
          {/* Project Settings is a nested layout route (persistent side-nav
                + <Outlet/>), so it's declared explicitly rather than via the
                flat route map. */}
          <Route path=":projectId/settings" element={<ProjectSettings />}>
            <Route
              index
              element={
                <Navigate to={DEFAULT_PROJECT_SETTINGS_SECTION.value} replace />
              }
            />
            <Route
              path="live-agent-platforms"
              element={<LiveAgentPlatforms />}
            />
            <Route
              path="reporting-api-credentials"
              element={<ReportingApiCredentials />}
            />
            <Route path="data-privacy" element={<DataPrivacy />} />
            <Route path="advanced-settings" element={<AdvancedSettings />} />
          </Route>
          {/* Last, so every real route above is tried first. A splat route is
              ranked below any route that names a path. */}
          <Route path="*" element={<NotFound />} />
        </Route>
      )
    )
  );

  if (!router) return <Loader />;

  return <ReactRouterProvider router={router} useTransitions={false} />;
};

export function WithLoadingState<T extends StudioPageProps>(
  WrappedComponent: React.ComponentType<T>
) {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithLoadingState = (props: T) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sawLoading, setSawLoading] = useState(false);
    const announceReady = useLiveRegionReady();
    const { t } = useTranslation();
    const { label: pageLabel } = useCurrentRoute();
    const currentPageDataLoading = useLoadingState() || isLoading;
    // Two ways the user's data fails to arrive: `GetUserInfo` (the user machine
    // is in `loggedIn.error`) and `GetUserProjects` (PSTUDIO-4492, item 6 — the
    // Pro Studio machine raises `userProjectsLoadFailed`). Both end on the same
    // retry screen and the same retry, `user.refetch`; the rule that joins them
    // is `resolvePageLoadView`.
    const userProjectsFailed = useProStudioCtxSelector(
      (ctx) => ctx.userProjectsLoadFailed === true
    );
    const userInfoFailed = useUserStateMatches('loggedIn.error');
    const view = resolvePageLoadView({
      loading: currentPageDataLoading,
      userInfoFailed,
      userProjectsFailed,
    });

    // The loader below stays mounted and is only hidden. A live region inside
    // it would be filled while `display: none` and would then have nothing new
    // to say when the spinner appears. This region stays in the document; its
    // text changes. "Loaded" is said only after this mount has actually been
    // loading, so a page that never loads does not announce it on entry.
    useEffect(() => {
      if (currentPageDataLoading) setSawLoading(true);
    }, [currentPageDataLoading]);

    const statusText = !announceReady
      ? ''
      : currentPageDataLoading
        ? t('pro-studio/common:pageStatus.loading')
        : sawLoading
          ? t('pro-studio/common:pageStatus.loaded')
          : '';

    // After every hook above: an early return before the live-region effect changes
    // the hook order between renders. PSTUDIO-4099 S30: a failed GetUserInfo used to leave this on the loader
    // for good. `user.refetch` re-enters `loggedIn.loading`, and on success
    // `getUserData` (still subscribed, since it never ran) sends
    // `updateUserInfo`, which moves the machine on.
    if (view === 'user-info-error') {
      return (
        <PageBody background="none">
          <ErrorState
            label={String(t('pro-studio/common:userInfoLoadError.title'))}
            body={String(t('pro-studio/common:userInfoLoadError.body'))}
            actionLabel={String(t('pro-studio/common:userInfoLoadError.retry'))}
            onAction={() => {
              // Lower the project failure first, so the loader shows while the
              // retry runs; a second failure raises it again.
              sendProStudioEvent({
                type: 'setUserProjectsLoadFailed',
                failed: false,
              });
              sendUserEvent({ type: 'user.refetch' });
            }}
          />
        </PageBody>
      );
    }

    return (
      <>
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="u-visually-hidden"
        >
          {statusText}
        </span>
        <div
          style={{
            ...centeredContainerStyles,
            display: currentPageDataLoading ? 'flex' : 'none',
          }}
        >
          <Loader relative announce={false} />
        </div>
        <PageBody
          {...props.pageBodyProps}
          style={{
            display: currentPageDataLoading ? 'none' : 'block',
          }}
        >
          <WrappedComponent
            {...props}
            label={pageLabel}
            loading={currentPageDataLoading}
            setLoading={setIsLoading}
          />
        </PageBody>
      </>
    );
  };

  ComponentWithLoadingState.displayName = `WithLoadingState(${displayName})`;

  return ComponentWithLoadingState;
}
